import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Reveal, OceanTag } from "./kit";
import { OCEAN_PARTNER_CATEGORIES, OCEAN_PROSPECTIVE_ORGS } from "../../data/oceanContent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY = { organisation: "", contact_person: "", email: "", category: "", contribution: "", geographic_focus: "", message: "" };

const inputCls =
  "w-full bg-onavy/70 border border-oreef/20 text-oseaglass placeholder:text-oslate/70 px-4 py-3 text-sm focus:outline-none focus:border-oreef transition-colors duration-300";

export default function OceanPartnerships() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (form.organisation.trim().length < 2) errs.organisation = "Organisation is required";
    if (form.contact_person.trim().length < 2) errs.contact_person = "Contact person is required";
    if (!EMAIL_RE.test(form.email)) errs.email = "A valid professional email is required";
    if (!form.category) errs.category = "Select a partnership category";
    if (form.contribution.trim().length < 2) errs.contribution = "Proposed contribution is required";
    if (form.geographic_focus.trim().length < 2) errs.geographic_focus = "Geographic focus is required";
    if (form.message.trim().length < 10) errs.message = "Message must be at least 10 characters";
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setSending(true);
    try {
      await axios.post(`${API}/ocean/enquiries/partnership`, form);
      toast.success("Partnership enquiry received. The Arjuna team will review it.");
      setForm(EMPTY);
      setErrors({});
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="partners" data-testid="ocean-partnerships-section" className="relative py-24 lg:py-32">
      <div className="relative max-w-[1600px] mx-auto px-4 sm:px-8 lg:px-12">
        <Reveal>
          <OceanTag index="07" label="Partnerships & Sponsorship" />
          <h2 className="font-display font-bold uppercase tracking-tight text-4xl sm:text-5xl lg:text-6xl text-oseaglass leading-[0.95] max-w-3xl">
            Help build India's <span className="text-oreef">ocean intelligence</span>
          </h2>
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-oseaglass/70 leading-relaxed">
            We invite collaboration from research institutions, dive operators, conservation organisations, satellite and
            geospatial partners, ocean-technology companies and sponsors.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-14">
          <div className="lg:col-span-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {OCEAN_PARTNER_CATEGORIES.map((c, i) => (
                <Reveal key={c} delay={0.05 * i}>
                  <div className="border border-oreef/15 bg-oabyss/40 p-5 h-full hover:border-oreef/40 transition-colors duration-300" data-testid={`ocean-partner-category-${i}`}>
                    <span className="font-mono text-[10px] text-ocoral tracking-[0.3em]">0{i + 1}</span>
                    <p className="font-display font-semibold text-base text-oseaglass mt-2 leading-tight tracking-wide">{c}</p>
                  </div>
                </Reveal>
              ))}
              </div>

            <Reveal delay={0.15}>
              <div className="mt-8 border border-ogold/25 bg-ogold/5 p-6" data-testid="ocean-prospective-orgs">
                <div className="font-mono text-[10px] tracking-[0.25em] text-ogold mb-4">PROSPECTIVE ORGANISATIONS TO INVESTIGATE — NOT PARTNERS</div>
                <ul className="space-y-2">
                  {OCEAN_PROSPECTIVE_ORGS.map((o) => (
                    <li key={o} className="text-sm text-oseaglass/70 flex gap-3"><span className="text-ogold shrink-0">·</span>{o}</li>
                  ))}
                </ul>
                <p className="mt-5 text-xs font-mono text-oslate leading-relaxed tracking-wide">
                  No logos shown. No endorsement implied. No funding, approval or research agreement has been secured with
                  any of these organisations.
                </p>
              </div>
            </Reveal>
          </div>

          <Reveal className="lg:col-span-6" delay={0.1}>
            <form onSubmit={submit} noValidate className="border border-oreef/20 bg-oabyss/50 backdrop-blur-md p-7 sm:p-9" data-testid="ocean-partnership-form">
              <div className="font-mono text-[10px] tracking-[0.3em] text-oreef mb-7">PARTNERSHIP ENQUIRY — SECURE SUBMISSION</div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="opf-org" className="block font-mono text-[10px] tracking-[0.2em] text-oslate mb-2">ORGANISATION *</label>
                  <input id="opf-org" data-testid="ocean-partnership-input-organisation" className={inputCls} value={form.organisation} onChange={set("organisation")} placeholder="Institution or company" />
                  {errors.organisation && <p className="text-ocoral text-xs mt-1.5" data-testid="ocean-partnership-error-organisation">{errors.organisation}</p>}
                </div>
                <div>
                  <label htmlFor="opf-contact" className="block font-mono text-[10px] tracking-[0.2em] text-oslate mb-2">CONTACT PERSON *</label>
                  <input id="opf-contact" data-testid="ocean-partnership-input-contact" className={inputCls} value={form.contact_person} onChange={set("contact_person")} placeholder="Full name" />
                  {errors.contact_person && <p className="text-ocoral text-xs mt-1.5" data-testid="ocean-partnership-error-contact">{errors.contact_person}</p>}
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="opf-email" className="block font-mono text-[10px] tracking-[0.2em] text-oslate mb-2">PROFESSIONAL EMAIL *</label>
                <input id="opf-email" type="email" data-testid="ocean-partnership-input-email" className={inputCls} value={form.email} onChange={set("email")} placeholder="name@organisation.org" />
                {errors.email && <p className="text-ocoral text-xs mt-1.5" data-testid="ocean-partnership-error-email">{errors.email}</p>}
              </div>

              <div className="mt-5">
                <label htmlFor="opf-category" className="block font-mono text-[10px] tracking-[0.2em] text-oslate mb-2">PARTNERSHIP CATEGORY *</label>
                <select id="opf-category" data-testid="ocean-partnership-input-category" className={`${inputCls} appearance-none`} value={form.category} onChange={set("category")}>
                  <option value="" disabled>Select a category</option>
                  {OCEAN_PARTNER_CATEGORIES.map((c) => <option key={c} value={c} className="bg-onavy">{c}</option>)}
                </select>
                {errors.category && <p className="text-ocoral text-xs mt-1.5" data-testid="ocean-partnership-error-category">{errors.category}</p>}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-5">
                <div>
                  <label htmlFor="opf-contribution" className="block font-mono text-[10px] tracking-[0.2em] text-oslate mb-2">PROPOSED CONTRIBUTION *</label>
                  <input id="opf-contribution" data-testid="ocean-partnership-input-contribution" className={inputCls} value={form.contribution} onChange={set("contribution")} placeholder="e.g. data, logistics, dive support, sponsorship" />
                  {errors.contribution && <p className="text-ocoral text-xs mt-1.5" data-testid="ocean-partnership-error-contribution">{errors.contribution}</p>}
                </div>
                <div>
                  <label htmlFor="opf-geo" className="block font-mono text-[10px] tracking-[0.2em] text-oslate mb-2">GEOGRAPHIC FOCUS *</label>
                  <input id="opf-geo" data-testid="ocean-partnership-input-geo" className={inputCls} value={form.geographic_focus} onChange={set("geographic_focus")} placeholder="e.g. Lakshadweep, Gulf of Mannar" />
                  {errors.geographic_focus && <p className="text-ocoral text-xs mt-1.5" data-testid="ocean-partnership-error-geo">{errors.geographic_focus}</p>}
                </div>
              </div>

              <div className="mt-5">
                <label htmlFor="opf-message" className="block font-mono text-[10px] tracking-[0.2em] text-oslate mb-2">MESSAGE *</label>
                <textarea id="opf-message" rows={4} data-testid="ocean-partnership-input-message" className={`${inputCls} resize-y`} value={form.message} onChange={set("message")} placeholder="Tell us how you'd like to be involved" />
                {errors.message && <p className="text-ocoral text-xs mt-1.5" data-testid="ocean-partnership-error-message">{errors.message}</p>}
              </div>

              <button type="submit" disabled={sending} data-testid="ocean-partnership-submit-button"
                className="mt-8 w-full bg-ocoral text-onavy font-mono text-xs font-semibold uppercase tracking-[0.25em] py-4 clip-corner hover:bg-oseaglass transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed">
                {sending ? "Transmitting…" : "Discuss a Partnership"}
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
